import { css } from "@emotion/react"
import { useEffect, useState, useRef } from "react"
import { FormattedMessage } from "react-intl"
import { colors, fonts, PitchBg } from "shared-ui"
import NumberScroller from "../_control/NumberScroller"
import { useIntl } from "react-intl"
import Cta from "../_control/Cta"
import { transparentize } from "polished"
import Orientation from "../test/Orientation"


// event zoneresults have axis as follows:
//     X
//    ------------------->
// Y |
//   |
//   |
//   |
//   \/

/**
 * @param {Object} props
 * @param {[number, number]} props.grid - The grid dimensions [width, height]
 * @param {string} [props.backgroundType] - The background type
 * @param {string} [props.orientation] - The background type
 * @param {array} [props.zoneResults] - Zoneresults array of objects, sorted by Y and X
 * @param {function} [props.setZoneResults] - State setter
 */

export default function LightingZonePicker({
    grid = [0, 0],
    backgroundType,
    orientation,
    zoneResults,
    setZoneResults,
}) {

    const { formatMessage } = useIntl()

    const [selectedDuration, setSelectedDuration] = useState(1)

    const lastGridRef = useRef(grid)

    useEffect(() => {
        const lastGrid = lastGridRef.current
        if (lastGrid[0] === grid[0] && lastGrid[1] === grid[1]) {

        } else {
            setZoneResults(getInitState(grid[0], grid[1]))
        }
        lastGridRef.current = grid
    }, [grid])

    const handleSelect = (x, y) => {
        setZoneResults(prev => prev.map(z => {
            return z.posX === x && z.posY === y ?
                ({ ...z, selected: !z.selected })
                : z
        }))
    }

    const handleToggleAll = () => {
        setZoneResults(prev => prev.some(z => !z.selected) ?
            prev.map(z => ({ ...z, selected: true }))
            : prev.map(z => ({ ...z, selected: false }))
        )
    }

    const handleApplyDuration = () => {
        setZoneResults(prev => prev.map(z => z.selected ? ({
            ...z,
            selected: false,
            timeTaken: selectedDuration * 60 // convert to minutes
        }) : z))
    }

    const handleClearDuration = () => {
        setZoneResults(prev => prev.map(z => z.selected ? ({ ...z, selected: false, timeTaken: null }) : z))
    }

    const hasSelected = zoneResults.some(z => z.selected)

    return <div css={css`
        display: flex; 
        user-select: none;
        @media screen and (max-width: 800px) {
            flex-direction: column;
        }
    `}>
        <div css={css`width: min-content;`}>
            <div css={css`width: 100%; display: grid; place-items: center;`}>
                <button onClick={handleToggleAll} css={style.toggle}>
                    <FormattedMessage id='toggleAll' />
                </button>
            </div>
            <div css={style.fieldWrapper}>
                <PitchBg
                    type={backgroundType}
                    css={style.field}
                />
                <div css={[
                    style.field,
                    css`
                        grid-template-columns: repeat(${grid[0]}, 1fr);
                        grid-template-rows: repeat(${grid[1]}, 1fr);
                    `
                ]}>
                    {zoneResults.map((zone, i) => <div
                        key={i}
                        onClick={() => handleSelect(zone.posX, zone.posY)}
                        css={[
                            css`background: ${zone.selected ? transparentize(0.5, colors.main1) : 'transparent'};`,
                            style.cell,
                        ]}
                    >
                        {zone.timeTaken ? Math.round(zone.timeTaken * 10 / 60) / 10 : ''}
                    </div>)}

                </div>
            </div >
            <Orientation
                css={style.compass}
                heading={orientation}
            />
        </div>

        <div css={css`
            display: flex; 
            flex-direction: column;
            padding: 2em;
            opacity: ${!hasSelected ? 0.5 : 1};
        `}>
            <div>
                {formatMessage({ id: 'duration' })}
            </div>

            <NumberScroller
                minVal={0.5}
                maxVal={24}
                state={selectedDuration}
                unit={formatMessage({ id: 'hours' })}
                step={0.5}
                setState={setSelectedDuration}
            />

            <div css={css`
                display: flex;
                flex-direction: column; 
                
                `}>

                <Cta
                    disabled={!hasSelected}
                    onClick={handleApplyDuration}
                >
                    {formatMessage({ id: 'confirm' })}
                </Cta>
                <Cta
                    disabled={!hasSelected}
                    onClick={handleClearDuration}
                    css={css`margin-top: 1em; border-color: ${colors.light};`}
                >
                    {formatMessage({ id: 'clearDataGeneric' })}
                </Cta>
            </div>
        </div>
    </div>
}

function getInitState(x, y) {
    const zones = []

    for (let i = 0; i < y; i++) {
        for (let j = 0; j < x; j++) {
            zones.push({
                posX: j + 1,
                posY: i + 1,
                selected: false,
                timeTaken: null,
            })
        }
    }

    return zones
}



const style = {
    fieldWrapper: css`
        background-color: #363E45;
        width: 450px;
        height: 300px;
        box-shadow: 0px 0px 1px 2px ${colors.eventLight};
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        @media screen and (max-width: 600px) {
            width: 225px;
            height: 150px;
        }
    `,
    field: css`
        width: 100%;
        height: 100%;
        position: absolute;
        display: grid;
    `,
    toggle: css`
        background: ${colors.eventLight};
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
        color: ${colors.soft};
        font-family: ${fonts.main};
        border: none;
        padding: 0.4em 0.8em;
        cursor: pointer;

        &:hover {
            color: ${colors.white};
            background: ${colors.eventDark};
        }

        &:active {
            color: ${colors.soft};
            background: ${colors.eventLight};
        }
    `,
    cell: css`
        outline: 1px solid rgba(255,255,255,0.075);
        display: grid;
        place-items:center; 
        font-weight: bold;
        text-shadow: 1px 1px 1px black;
        cursor: pointer;

        &:hover {
            outline: 1px solid ${colors.main1};
        }
    `,
    compass: css`
        position: relative;
        height: 2em;
        width: 2em;
        bottom: -1em;
        left: calc(50% - 1em);
        background-color: rgba(44,52,58);
        box-shadow: none;
    `,
}